'use client';

import { useCallback, useDeferredValue, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { valuesEqual } from '@macaron-a2ui/core/v0_8';
import { chatDispatcher } from '@/dispatcher/ChatDispatcher';
import { parseA2UIRawDeltaStream, type A2UIPreviewRendererInput } from '@macaron-a2ui/core/v0_8';
import { deriveA2UIMessagesFromCanvasSurface } from '@macaron-a2ui/core/v0_8';
import { readA2uiLocalHandlerWebUrlValue } from '@/lib/utils/a2uiLocalHandler';
import type { CanvasSurface, CanvasUserAction } from '@/lib/vo/chat';
import { MacaronCanvasRenderer } from '@macaron-a2ui/react/v0_8';
import { getMacaronCardSurfaceStyle, MACARON_CARD_SURFACE_CLASSNAME, renderMacaronComponent } from '@macaron-a2ui/catalog';

interface A2UIMessageRendererProps {
  surfaceId: string;
  originMessageId: string;
  surfaceVersion?: number;
  messages?: Record<string, unknown>[];
  canvasPayload?: Record<string, unknown>;
  rawStream?: string;
}
type A2UIMessageRendererViewProps = A2UIMessageRendererProps & {
  onAction?: (actionName: string, sourceComponentId: string, context?: Record<string, unknown> | null) => void;
  onLocalHandler?: (operation: string, sourceComponentId: string, value?: unknown) => void;
};

function hasPreviewContent(input: A2UIPreviewRendererInput | null | undefined): input is A2UIPreviewRendererInput {
  return Boolean(input?.messages?.length || input?.canvasPayload);
}

function isSamePreviewInput(left: A2UIPreviewRendererInput | null, right: A2UIPreviewRendererInput | null) {
  if (left === right) return true;
  if (!left || !right) return false;
  return (
    left.surfaceId === right.surfaceId &&
    left.surfaceVersion === right.surfaceVersion &&
    left.originMessageId === right.originMessageId &&
    valuesEqual(left.messages || [], right.messages || []) &&
    valuesEqual(left.canvasPayload || null, right.canvasPayload || null)
  );
}

function A2UIMessageRendererView({ surfaceId, messages, canvasPayload, rawStream, onAction, onLocalHandler }: A2UIMessageRendererViewProps) {
  const finalA2uiMessages = useMemo(() => {
    const list = messages || [];
    if (!Array.isArray(list)) {
      return [];
    }
    const filtered = list.filter((item) => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      const record = item as Record<string, unknown>;
      return Boolean(record.surfaceUpdate || record.dataModelUpdate || record.beginRendering || record.deleteSurface);
    });
    if (filtered.length > 0) {
      return filtered;
    }
    // 后端可能存 `a2ui_messages: []`，真实内容在 `payload.objects`（与 derive 逻辑一致）
    const fromPayload = deriveA2UIMessagesFromCanvasSurface({
      payload: canvasPayload,
    } as CanvasSurface);
    return fromPayload;
  }, [messages, canvasPayload]);

  const deferredRawStream = useDeferredValue(rawStream);
  const lastRenderablePreviewRef = useRef<A2UIPreviewRendererInput | null>(null);
  const previewInput = useMemo(() => {
    if (finalA2uiMessages.length > 0 || canvasPayload) {
      lastRenderablePreviewRef.current = null;
      return null;
    }
    if (!deferredRawStream?.trim()) return lastRenderablePreviewRef.current;
    const nextPreview = parseA2UIRawDeltaStream(deferredRawStream).rendererInput;
    if (!hasPreviewContent(nextPreview)) return lastRenderablePreviewRef.current;
    if (isSamePreviewInput(lastRenderablePreviewRef.current, nextPreview)) return lastRenderablePreviewRef.current;
    lastRenderablePreviewRef.current = nextPreview;
    return nextPreview;
  }, [canvasPayload, deferredRawStream, finalA2uiMessages.length]);

  const resolvedMessages = finalA2uiMessages.length > 0 ? finalA2uiMessages : previewInput?.messages || [];
  const resolvedCanvasPayload = canvasPayload || previewInput?.canvasPayload;

  const hasRenderableContent = resolvedMessages.length > 0 || Boolean(resolvedCanvasPayload);

  if (!hasRenderableContent) {
    return null;
  }

  return (
    <div className="w-full min-w-0 font-sans">
      {/* MacaronCanvasRenderer already owns the canvas shadow bleed. Keep this wrapper width-neutral and only reset typography to sans. */}
      {/* Key by surfaceId so navigating between gallery details (same route, params change) remounts the renderer instead of reusing a tree whose `data` useState initializer already ran with the previous example's parsedData — the stale data otherwise raced with the data-sync useEffect for one frame, briefly flashing the right values then snapping back to wrong ones. */}
      <MacaronCanvasRenderer
        key={surfaceId}
        payload={resolvedCanvasPayload}
        messages={resolvedMessages}
        renderComponent={renderMacaronComponent}
        onAction={onAction}
        onLocalHandler={onLocalHandler}
        fallbackCardClassName={MACARON_CARD_SURFACE_CLASSNAME}
        fallbackCardStyle={getMacaronCardSurfaceStyle()}
      />
    </div>
  );
}

export function InteractiveA2UIMessageRenderer({ surfaceId, originMessageId, surfaceVersion = 1, ...props }: A2UIMessageRendererProps) {
  const handleMacaronAction = useCallback(
    (actionName: string, sourceComponentId: string, context?: Record<string, unknown> | null) => {
      if (!actionName || !sourceComponentId) return;
      const payload: CanvasUserAction = {
        protocol_version: 'a2ui-canvas/v1',
        origin_message_id: originMessageId,
        surface_id: surfaceId,
        surface_version: surfaceVersion,
        client_action_id: uuidv4(),
        action_name: actionName,
        source_component_id: sourceComponentId,
        context: context ?? null,
        occurred_at: new Date().toISOString(),
      };
      chatDispatcher.enqueueCanvasUserAction(payload);
    },
    [originMessageId, surfaceId, surfaceVersion],
  );
  const handleMacaronLocalHandler = useCallback((operation: string, sourceComponentId: string, value?: unknown) => {
    if (!operation || !sourceComponentId || operation !== 'open_url') return;
    const normalizedUrl = readA2uiLocalHandlerWebUrlValue(value);
    if (!normalizedUrl || typeof window === 'undefined') return;
    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
  }, []);
  return <A2UIMessageRendererView {...props} surfaceId={surfaceId} originMessageId={originMessageId} surfaceVersion={surfaceVersion} onAction={handleMacaronAction} onLocalHandler={handleMacaronLocalHandler} />;
}

export default function A2UIMessageRenderer(props: A2UIMessageRendererProps) {
  return <A2UIMessageRendererView {...props} />;
}
